import styled from '@emotion/styled';
import type { ReactNode } from 'react';
import { Color } from '../../../features/shared/styles/Color';
import type { ITheme, IThemedProps } from '../../themer';
import themed from '../../themer/themed';
import Responsive from '../../utils/Responsive';
import { FixedGridItem } from '../Grid';
import { getIconLinkHoverStyle } from '../Link';
import type { ICardContainerContextValues } from './CardContainerContext';
import type { CardOrientation } from './CardsContainer';
import { StyledCardChildren } from './StyledCardChildren';
import { StyledCardContent } from './StyledCardContent';
import { StyledCardLink } from './StyledCardLink';
import { StyledCardPicture } from './StyledCardPicture';
import { StyledCardTitle } from './StyledCardTitle';

export interface IStyledCardProps
	extends ICardContainerContextValues,
		IThemedProps {
	children?: ReactNode;
	titleAsLinkCard?: boolean;
	fullyClickable?: boolean;
}

const getOrientationStyle = (orientation: CardOrientation) => {
	if (orientation === 'horizontal') {
		return `
			flex-direction: row;
			align-items: start;

			${StyledCardPicture} {
				max-width: 33.333%;
				img {
					width: auto;
					max-width: 100%;
				}
			}

			${StyledCardContent} {
				margin-top: 0;
				margin-bottom: 0;
				margin-left: 16px;
			}

			@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
				align-items: center;
			}
		`;
	}

	return `
		flex-direction: column;
	`;
};

const getTitleFontSizeStyle = (size: number) => `
	${StyledCardTitle} {
		font-size: ${size}px;
	}`;

const getLinkHiddenStyle = (isHidden: boolean) => `
		${StyledCardLink} {
			display: ${isHidden ? 'none' : ''}
		}
	`;

const getChildrenHiddenStyle = (isHidden: boolean) => `
		${StyledCardChildren} {
			display: ${isHidden ? 'none' : ''}
		}
	`;

export const StyledCard = styled(FixedGridItem)<IStyledCardProps, ITheme>`
	display: flex;
	transition: ${themed(({ card }) => card.transition)};
	box-shadow: ${themed(({ card }, { fullyClickable }) =>
		fullyClickable ? card.fullyClickable.boxShadow : card.boxShadow,
	)};
	cursor: ${({ fullyClickable }) => (fullyClickable ? 'pointer' : '')};
	text-align: left;
	border-radius: 12px;
	overflow: hidden;

	${themed((_, { fullyClickable }) =>
		fullyClickable
			? `
				&:hover {
					color: ${Color.TextWhite} !important;
					background-color: ${Color.PrimaryDefault};

					* {
						color: ${Color.TextWhite} !important;
					}

					&:after {
						display: none;
					}

					g {
						stroke: ${Color.TextWhite};
					}
				}
				&:focus-visible,
				&:has(> a:focus-visible) {
					outline: 2px solid #430042;
					color: ${Color.TextWhite} !important;
					background-color: ${Color.PrimaryDefault};

					* {
						color: ${Color.TextWhite} !important;
					}

					&:after {
						display: none;
					}

					g {
						stroke: ${Color.TextWhite};
					}
				}

				> a:focus-visible {
					outline: none;
				}
		`
			: '',
	)}

	&:hover,
	&:has(> a:focus-visible) {
		box-shadow: ${themed(({ card }, { fullyClickable }) =>
			fullyClickable
				? card.fullyClickable.hover.boxShadow
				: card.hover.boxShadow,
		)};

		${StyledCardLink}, .non-clickable-card-link {
			${({ fullyClickable, ...props }) =>
				fullyClickable && getIconLinkHoverStyle(props)};
		}

		${StyledCardTitle} {
			${({ titleAsLinkCard, theme }) =>
				titleAsLinkCard && `color: ${theme.card.hover.color}`}
		}
	}

	${StyledCardContent} {
		flex-grow: 1;
		width: 100%;
		padding: ${themed(({ card }, { fullyClickable }) =>
			fullyClickable ? card.fullyClickable.contentMargin : card.contentMargin,
		)};
	}

	${Responsive.getStyles<CardOrientation, IStyledCardProps>(
		'cardOrientation',
		getOrientationStyle,
	)};
	${Responsive.getStyles<number, IStyledCardProps>(
		'titleFontSize',
		getTitleFontSizeStyle,
	)};
	${Responsive.getStyles<boolean, IStyledCardProps>(
		'linkHidden',
		getLinkHiddenStyle,
	)};
	${Responsive.getStyles<boolean, IStyledCardProps>(
		'childrenHidden',
		getChildrenHiddenStyle,
	)};
`;
